import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import { Dashboard } from '../components/dashboard/dashboard.tsx';
import { Toolbar } from '../components/toolbar/toolbar.tsx';
import { ToolbarTitle } from '../components/toolbar/toolbar-title.tsx';
import { Layout } from '../components/layout/layout.tsx';
import { useEffect, useState } from 'react';
import type { SamplerUser, SamplingJobMetric, SamplingJobStatusMetric } from '../services/api.types.ts';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { ErrorRender } from '../components/render/error-render.tsx';
import { getSamplers, getSamplingJobMetrics, getSamplingJobs, getSamplingJobStatusMetrics } from '../services/api.ts';
import { getTargetMonth, getWeekRange } from '../utils/date.ts';
import { JobWeekTile } from '../content/dashboard/job-week-tile.tsx';
import { JobStatusTile } from '../content/dashboard/job-status-tile.tsx';
import { SamplingMetricsTile } from '../content/dashboard/sampling-metrics-tile.tsx';
import { ActiveSamplersTile } from '../content/dashboard/active-samplers-tile.tsx';
import styles from './home-page.module.scss';

const BREADCRUMB_ITEMS = [
  {
    id: 'homepage',
    text: 'Home',
    url: '/',
    icon: <FontAwesomeIcon icon={faHome} size={'lg'} />,
  },
];

const CURRENT_SEASON_START = getTargetMonth(5);
const CURRENT_WEEK = getWeekRange(0);

export function HomePage() {
  const [samplerUsers, setSamplerUsers] = useState<SamplerUser[]>([]);
  const [jobMetrics, setJobMetrics] = useState<SamplingJobMetric[]>([]);
  const [jobStatusMetrics, setJobStatusMetrics] = useState<SamplingJobStatusMetric[]>([]);
  const [plannedJobs, setPlannedJobs] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [samplers, jobs, jobMetrics, statusMetrics] = await Promise.all([
          getSamplers({ showInactive: false }),
          getSamplingJobs({
            showInactive: false,
            showUnassigned: false,
            startDate: CURRENT_WEEK.start,
            endDate: CURRENT_WEEK.end,
          }),
          getSamplingJobMetrics({ startDate: CURRENT_SEASON_START }),
          getSamplingJobStatusMetrics({ startDate: CURRENT_SEASON_START }),
        ]);
        if (isMounted) {
          setSamplerUsers(samplers);
          setPlannedJobs(jobs.length);
          setJobMetrics(jobMetrics);
          setJobStatusMetrics(statusMetrics);
        }
      } catch (error) {
        console.error('Error load dashboard:', error);
        if (isMounted) {
          setError('Unable to load dashboard');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Layout breadcrumb={BREADCRUMB_ITEMS}>
      <Toolbar>
        <ToolbarTitle flex={1}>Sampling Dashboard</ToolbarTitle>
      </Toolbar>
      {loading ? (
        <LoaderRender message={'Loading...'} />
      ) : error.length > 0 ? (
        <ErrorRender message={error} />
      ) : (
        <Dashboard
          className={styles.dashboard}
          columns={4}
          items={[
            {
              defaultPosition: {
                col: 1,
                row: 1,
                colSpan: 1,
                rowSpan: 1,
              },
              item: <JobStatusTile data={jobStatusMetrics} />,
              reorderable: false,
              resizable: false,
            },
            {
              defaultPosition: {
                col: 2,
                row: 1,
                colSpan: 1,
                rowSpan: 1,
              },
              item: <JobWeekTile weekStart={CURRENT_WEEK.start} plannedCount={plannedJobs} />,
              reorderable: false,
              resizable: false,
            },
            {
              defaultPosition: {
                col: 3,
                row: 1,
                colSpan: 2,
                rowSpan: 3,
              },
              item: <SamplingMetricsTile data={jobMetrics} />,
              reorderable: false,
              resizable: false,
            },
            {
              defaultPosition: {
                col: 1,
                row: 2,
                colSpan: 2,
                rowSpan: 2,
              },
              item: <ActiveSamplersTile data={samplerUsers} />,
              reorderable: false,
              resizable: false,
            },
          ]}
        />
      )}
    </Layout>
  );
}
